import React, { useEffect, useReducer, useRef } from "react";

import Constants from "./Constants";
import PathMap from "./Paths";
import ViewMap from "./ViewMap";
import { loadDraft, saveDraft } from "./draftStorage";

import { useAccountState } from "../../hooks/query/useAccount";
import AppConstants from "../../constants/AppConstants";
import useDomainSetup from "./hooks/useDomainSetup";
import useSubmitSetupRequest from "../blogsetup/hooks/useSubmitSetupRequest";
import { invalidateUserBlogDomains } from "../../hooks/query/useUserBlogDomains";
import useUserAvailableDomains from "../../hooks/query/useUserAvailableDomains";
import UserActions from "../../actions/UserActions";
import SiteActions from "../../actions/SiteActions";
import { getSiteId } from "../../hooks/query/useSite";

import styles from "./DomainSetupWizard.module.css";

function getInitialPath(allowedBlogType) {
  return allowedBlogType === AppConstants.BLOG_TYPE_ADVANCED
    ? Constants.PATH_ADV_NEW
    : allowedBlogType === AppConstants.BLOG_TYPE_BASIC
      ? Constants.PATH_BASIC_BLOG
      : Constants.PATH_NO_BLOG;
}

const initialState = {
  path: null,
  pathIndex: 0,
  view: Constants.VIEW_DOMAIN_INFO,

  domainConfig: {
    domain_name: "",
    owns_domain: true,
    self_config: "Y",
    configure_domain: Constants.CONFIGURE_DOMAIN_NEW,
  },
  blogSetupData: {
    blog_domain_name: "",
    blog_url: "",
    blog_admin_url: "",
    blog_admin_user: "",
    blog_admin_pass: "",
    blog_admin_verified: undefined,
    blog_new_url: "",
  },

  blogSubmitError: false,
  blogSubmitErrorMessage: null,

  userId: undefined,
  allowedBlogType: undefined,
};

function reducer(state, action) {
  switch (action.type) {
    case "next": {
      const steps = PathMap[state.path];
      const nextIndex = Math.min(steps.length - 1, state.pathIndex + 1);
      const nextView = steps[nextIndex];
      const newState = { ...state, pathIndex: nextIndex, view: nextView };
      return action.payload
        ? { ...newState, blogSetupData: { ...newState.blogSetupData, ...action.payload } }
        : newState;
    }
    case "prev": {
      const steps = PathMap[state.path];
      const prevIndex = Math.max(0, state.pathIndex - 1);
      return { ...state, pathIndex: prevIndex, view: steps[prevIndex] };
    }
    case "choosePath": {
      const newPath = action.payload.path;
      const steps = PathMap[newPath];
      const currentIndex = steps.indexOf(state.view);
      const nextIndex = Math.min(steps.length - 1, currentIndex + 1);
      return {
        ...state,
        path: newPath,
        pathIndex: nextIndex,
        view: steps[nextIndex],
      };
    }
    case "updateDomain":
      return { ...state, domainConfig: { ...state.domainConfig, ...action.payload } };
    case "updateBlog":
      return { ...state, blogSetupData: { ...state.blogSetupData, ...action.payload } };
    case "domainSubmitted": {
      const { dns, ...rest } = action.payload;
      return {
        ...state,
        domainConfig: { ...state.domainConfig, ...rest },
        dns,
      };
    }
    case "blogSubmitError":
      return {
        ...state,
        blogSubmitError: true,
        blogSubmitErrorMessage: action.payload?.message,
      };
    default:
      return state;
  }
}

function StepMenuItem({ active, text }) {
  return (
    <li className={`${styles["nav-item"]} ${active ? styles.active : ""}`}>
      {text}
    </li>
  );
}

function DomainSetupWizard({ onComplete }) {
  const user = useAccountState();
  const allowedBlogType = user.get("allowedBlogType");
  const domain = user.get("domain");
  const userId = user.get("user_id");

  const [state, dispatch] = useReducer(reducer, initialState, (s) => {
    const path = getInitialPath(allowedBlogType);
    const base = {
      ...s,
      path,
      view: PathMap[path][0],
      userId,
      allowedBlogType,
      domainConfig: {
        ...s.domainConfig,
        domain_name: domain || "",
      },
      blogSetupData: {
        ...s.blogSetupData,
        blog_domain_name: domain || "",
        blog_new_url: domain ? `${domain}${Constants.DEFAULT_BLOG_SUBPATH}` : "",
      },
    };

    // Resume a saved migration only if the user hasn't already submitted the
    // migration request (which is the moment user.blogId gets populated). The
    // draft is only ever written for PATH_ADV_MIGRATE past the first POST.
    if (user.get("blogId")) return base;
    const draft = loadDraft(userId);
    if (!draft || draft.path !== Constants.PATH_ADV_MIGRATE) return base;

    return {
      ...base,
      path: draft.path,
      pathIndex: draft.pathIndex,
      view: draft.view,
      domainConfig: { ...base.domainConfig, ...(draft.domainConfig || {}) },
      blogSetupData: { ...base.blogSetupData, ...(draft.blogSetupData || {}) },
      dns: draft.dns,
    };
  });

  // Persist only after the first POST has completed — i.e. on the migration
  // path, on the MIGRATION_DETAILS step or later. Earlier steps don't need it
  // (no server-side side effects yet) and persisting them would surface a
  // resume CTA for a user who just opened the modal and bailed.
  useEffect(() => {
    if (user.get("blogId")) return;
    const isMigrationPath = state.path === Constants.PATH_ADV_MIGRATE;
    const stepIndex = PathMap[state.path]?.indexOf(Constants.VIEW_MIGRATION_DETAILS) ?? -1;
    const passedFirstPost = stepIndex >= 0 && state.pathIndex >= stepIndex;
    if (!isMigrationPath || !passedFirstPost) return;
    saveDraft(userId, state);
  }, [userId, state, user]);

  const [submitDomain, { isPending: isDomainSubmitting, isError: isDomainSubmitError, error: domainSubmitError }] = useDomainSetup();
  const [submitBlogRequest] = useSubmitSetupRequest();

  const { availableDomains } = useUserAvailableDomains();
  const didPrefillRef = useRef(false);
  useEffect(() => {
    if (didPrefillRef.current) return;
    if (!availableDomains || availableDomains.length !== 1) return;
    if (state.domainConfig.domain_name) return;
    didPrefillRef.current = true;
    dispatch({ type: "updateDomain", payload: { domain_name: availableDomains[0].domainName } });
  }, [availableDomains, state.domainConfig.domain_name]);

  const handleDomainSetupSuccess = (data, domainName) => {
    UserActions.trackEvent({
      event: "domain-setup",
      meta: { domain_name: domainName },
    });
    UserActions.updateConfiguration({ domain: data.domain.domain_name });
    if (data.domain.site_design_id) {
      SiteActions.domainConnect(data.domain);
    }
    dispatch({
      type: "domainSubmitted",
      payload: { domain_name: domainName, dns: data.domain.dns },
    });
  };

  const autoSubmitBlogSetup = async (domainName) => {
    try {
      const setupRequest = {
        migrationRequired: false,
        setupData: {
          blog_domain_name: domainName,
          blog_new_url: `${domainName}${Constants.DEFAULT_BLOG_SUBPATH}`,
        },
      };
      await submitBlogRequest(
        { userId, setupRequest },
        {
          onSuccess: (data) => {
            UserActions.updateConfiguration({
              blogId: data.blogDomain.wp_host_id,
              blogStatus: data.blogDomain.status,
            });
            SiteActions.domainConnectBlog(data.blogDomain);
            invalidateUserBlogDomains();
          },
        }
      );
    } catch (error) {
      dispatch({ type: "blogSubmitError", payload: { message: error?.message } });
    }
  };

  const actions = {
    goToNextStep: (data) => dispatch({ type: "next", payload: data }),
    goToPrevStep: () => dispatch({ type: "prev" }),
    updateDomain: (data) => dispatch({ type: "updateDomain", payload: data }),
    updateSetup: (data) => dispatch({ type: "updateBlog", payload: data }),
    choosePath: (data) => dispatch({ type: "choosePath", payload: data }),
    submitDomainSetup: async (extraConfig = {}, { autoAdvance = true, onSuccess, onError } = {}) => {
      const domainName = state.domainConfig.domain_name;
      const setupInfo = {
        ...state.domainConfig,
        ...extraConfig,
        site_design_id: getSiteId(),
      };
      await submitDomain(
        { userId, setupInfo },
        {
          onSuccess: (data) => {
            handleDomainSetupSuccess(data, domainName);
            if (state.path === Constants.PATH_BASIC_BLOG) {
              autoSubmitBlogSetup(domainName);
            }
            if (onSuccess) {
              onSuccess(data);
            } else if (autoAdvance) {
              dispatch({ type: "next" });
            }
          },
          onError: (error) => {
            if (onError) {
              onError(error);
            }
          },
        }
      );
    },
    close: () => {
      if (onComplete) onComplete();
    },
  };

  const steps = PathMap[state.path];
  const stepItems = steps
    ? steps.map((viewName) => {
        const entry = ViewMap.get(viewName);
        return entry ? (
          <StepMenuItem key={entry.view} text={entry.desc} active={entry.view === state.view} />
        ) : null;
      })
    : [];

  const viewEntry = ViewMap.get(state.view);

  const blogSetupCompat = {
    ...state,
    setupData: state.blogSetupData,
    migrationRequired: state.path === Constants.PATH_ADV_MIGRATE,
    isDomainSubmitting,
    isDomainSubmitError,
    domainSubmitError,
  };

  const viewComponent = viewEntry
    ? React.createElement(viewEntry.comp, { blogSetup: blogSetupCompat, actions })
    : null;

  return (
    <div className={styles["main-panel"]}>
      <div className={styles["navigation-pane"]}>
        <ol className={styles["navigation-list"]}>{stepItems}</ol>
      </div>
      <div className={styles["content-pane"]}>{viewComponent}</div>
    </div>
  );
}

export default DomainSetupWizard;
export { reducer, initialState, getInitialPath };
